import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as Highcharts from "highcharts";

import { RootState } from "../../../store-types";
import { ChartPeriod } from "../shared/enums/chart-period";
import {
  IFarmLandDataSet,
  IFarmLandsWeatherState,
} from "../shared/interfaces/farm-lands-weather-state";
import { subtractDaysFromDate } from "../shared/utils/subtract-days-from-date";

import { FARM_LANDS_WEATHER_MODULE_NAME } from "./constants";
import { getFarmLandsWeatherState } from "./farm-lands-weather.selector";
import { farmLandsApi } from "./farm-lands.rtkq";

export const farmLandsWeatherSlice = createSlice({
  name: FARM_LANDS_WEATHER_MODULE_NAME,
  initialState: {
    isLoading: false,
    filter: {
      stationId: null,
      period: ChartPeriod.Hourly,
      from: subtractDaysFromDate(1, new Date()),
      to: new Date(),
    },
    dataSets: {
      dates: [],
      data: [],
    },
    charts: [],
  } as IFarmLandsWeatherState,
  reducers: {
    setLoadingAction(state, action): void {
      state.isLoading = action.payload as boolean;
    },
    setPeriodAction(state, action): void {
      state.filter.period = action.payload as ChartPeriod;
    },
    setStationIdAction(state, action): void {
      state.filter.stationId = action.payload as string | null;
    },
    setFromAction(state, action): void {
      state.filter.from = action.payload as Date;
    },
    setToAction(state, action): void {
      state.filter.to = action.payload as Date;
    },
    setChartsAction(state, action): void {
      state.charts = action.payload;
    },
    setDataSetsAction(state, action): void {
      state.dataSets = action.payload as {
        dates: string[];
        data: IFarmLandDataSet[];
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchChartsAction.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(fetchChartsAction.pending, (state, _) => {
        state.isLoading = true;
      })
      .addCase(fetchChartsAction.rejected, (state, _) => {
        state.isLoading = false;
      });
  },
});

export const farmLandsWeatherReducer = farmLandsWeatherSlice.reducer;
export const {
  setStationIdAction,
  setDataSetsAction,
  setChartsAction,
  setToAction,
  setFromAction,
  setPeriodAction,
  setLoadingAction,
} = farmLandsWeatherSlice.actions;

// async actions
export const fetchChartsAction = createAsyncThunk<void, void, { state: RootState }>(
  `${FARM_LANDS_WEATHER_MODULE_NAME}/fetchChartsAction`,
  async (_, { getState, dispatch }) => {
    const state = getFarmLandsWeatherState(getState());

    // clear charts
    if (state.filter.stationId === null) {
      dispatch(setChartsAction([]));
      return;
    }

    const { stationId, from, to, period } = state.filter;
    let chartDtos: Highcharts.Options[];
    try {
      chartDtos = await dispatch(
        farmLandsApi.endpoints.fetchWeatherCharts.initiate(
          {
            stationId,
            from,
            to,
            period,
          },
          { subscribe: false, forceRefetch: true }
        )
      ).unwrap();
    } catch {
      chartDtos = [];
    }
    dispatch(setChartsAction(Array.isArray(chartDtos) ? chartDtos : []));
  }
);
